// Couche hors-ligne du Relevé d'étude : IndexedDB (champs + photos) + file de synchronisation.
// Objectif : ZÉRO perte. Tout est écrit localement immédiatement, puis poussé sur le
// serveur dès que la connexion est disponible (app ouverte). Une fois sur le serveur,
// les données sont visibles depuis n'importe quel appareil.

const DB_NAME = 'valnexo_study';
const DB_VERSION = 1;
const STORE_STUDIES = 'studies';      // { id, prospectId, data, rev, updatedAt, dirty }
const STORE_FILES = 'pendingFiles';   // { clientFileId, studyId, category, name, mime, blob, status }
const STORE_DELETES = 'pendingDeletes'; // { key, studyId, fileId }

let _dbPromise = null;

function openDB() {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_STUDIES)) db.createObjectStore(STORE_STUDIES, { keyPath: 'id' });
            if (!db.objectStoreNames.contains(STORE_FILES)) {
                const s = db.createObjectStore(STORE_FILES, { keyPath: 'clientFileId' });
                s.createIndex('byStudy', 'studyId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORE_DELETES)) db.createObjectStore(STORE_DELETES, { keyPath: 'key' });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return _dbPromise;
}

function tx(store, mode, fn) {
    return openDB().then((db) => new Promise((resolve, reject) => {
        const t = db.transaction(store, mode);
        const os = t.objectStore(store);
        const out = fn(os);
        t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
    }));
}

function reqToPromise(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

// ---------- Études (champs) ----------
export async function getLocalStudy(id) {
    return tx(STORE_STUDIES, 'readonly', (os) => os.get(id)).then((r) => r || null).catch(() => null);
}

export async function saveLocalStudy(study) {
    return tx(STORE_STUDIES, 'readwrite', (os) => { os.put(study); }).catch(() => {});
}

export async function markStudyClean(id, rev, updatedAt) {
    const s = await getLocalStudy(id);
    if (s) { s.dirty = false; s.rev = rev; s.updatedAt = updatedAt; await saveLocalStudy(s); }
}

// ---------- Validation en attente (hors-ligne) ----------
// Marque un relevé comme "à valider dès le retour du réseau". La validation s'exécutera
// automatiquement lors de la prochaine synchro (retour online / app ouverte).
export async function setValidatePending(id, pending) {
    const s = await getLocalStudy(id);
    if (s) { s.validatePending = !!pending; await saveLocalStudy(s); }
    else { await saveLocalStudy({ id, validatePending: !!pending }); }
}

export async function getValidatePending(id) {
    const s = await getLocalStudy(id);
    return !!(s && s.validatePending);
}

// ---------- Fichiers en attente ----------
export async function queuePendingFile(entry) {
    // entry: { clientFileId, studyId, category, name, mime, blob }
    return tx(STORE_FILES, 'readwrite', (os) => { os.put({ ...entry, status: 'pending' }); }).catch(() => {});
}

export async function getPendingFilesForStudy(studyId) {
    return openDB().then((db) => new Promise((resolve) => {
        const t = db.transaction(STORE_FILES, 'readonly');
        const idx = t.objectStore(STORE_FILES).index('byStudy');
        const req = idx.getAll(studyId);
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => resolve([]);
    })).catch(() => []);
}

export async function removePendingFile(clientFileId) {
    return tx(STORE_FILES, 'readwrite', (os) => { os.delete(clientFileId); }).catch(() => {});
}

export async function getAllPendingFiles() {
    return tx(STORE_FILES, 'readonly', (os) => os.getAll()).then((r) => r || []).catch(() => []);
}

// ---------- Suppressions en attente ----------
export async function queuePendingDelete(studyId, fileId) {
    return tx(STORE_DELETES, 'readwrite', (os) => { os.put({ key: `${studyId}:${fileId}`, studyId, fileId }); }).catch(() => {});
}
export async function getAllPendingDeletes() {
    return tx(STORE_DELETES, 'readonly', (os) => os.getAll()).then((r) => r || []).catch(() => []);
}
export async function removePendingDelete(key) {
    return tx(STORE_DELETES, 'readwrite', (os) => { os.delete(key); }).catch(() => {});
}

export const isOnline = () => (typeof navigator !== 'undefined' ? navigator.onLine !== false : true);

export { reqToPromise };
